import React from "react";
import {
  defaultThemePreference,
  designTemplates,
  themePresets,
} from "./themePresets";
import { saveThemePreferenceRemote } from "../services/firebaseRest";

const STORAGE_KEY = "ar-portfolio-theme";

const ThemeContext = React.createContext(null);

const modeTokens = {
  dark: {
    background: "#08111f",
    backgroundSecondary: "#0f172a",
    text: "#f8fafc",
    textMuted: "#cbd5e1",
    surface: "rgba(15, 23, 42, 0.58)",
    surfaceStrong: "rgba(15, 23, 42, 0.78)",
    surfaceSoft: "rgba(30, 41, 59, 0.62)",
    border: "rgba(255, 255, 255, 0.14)",
    shadow: "0 20px 60px rgba(2, 6, 23, 0.45)",
    overlay: "radial-gradient(circle at top, rgba(255,255,255,0.10), transparent 38%)",
  },
  light: {
    background: "#eef4ff",
    backgroundSecondary: "#dbeafe",
    text: "#0f172a",
    textMuted: "#334155",
    surface: "rgba(255, 255, 255, 0.62)",
    surfaceStrong: "rgba(255, 255, 255, 0.82)",
    surfaceSoft: "rgba(241, 245, 249, 0.86)",
    border: "rgba(148, 163, 184, 0.26)",
    shadow: "0 20px 60px rgba(15, 23, 42, 0.12)",
    overlay: "radial-gradient(circle at top, rgba(255,255,255,0.92), transparent 42%)",
  },
};

const templateTokens = {
  glass: {},
  single: {
    background: "#fbfaf7",
    backgroundSecondary: "#f3efe6",
    text: "#171412",
    textMuted: "#62584e",
    surface: "rgba(255, 255, 255, 0)",
    surfaceStrong: "rgba(255, 255, 255, 0.72)",
    surfaceSoft: "rgba(250, 247, 239, 0.92)",
    border: "rgba(23, 20, 18, 0.12)",
    shadow: "none",
    overlay: "linear-gradient(90deg, rgba(23,20,18,0.055) 1px, transparent 1px)",
  },
  premium: {
    background: "#090806",
    backgroundSecondary: "#17120d",
    text: "#fff9ef",
    textMuted: "#d5c6ae",
    surface: "rgba(18, 14, 10, 0.72)",
    surfaceStrong: "rgba(27, 21, 15, 0.92)",
    surfaceSoft: "rgba(39, 29, 19, 0.84)",
    border: "rgba(245, 158, 11, 0.24)",
    shadow: "0 28px 90px rgba(0, 0, 0, 0.5)",
    overlay:
      "radial-gradient(circle at 82% 8%, rgba(245,158,11,0.28), transparent 28%), linear-gradient(120deg, rgba(255,255,255,0.08), transparent 34%)",
  },
};

const loadStoredPreference = () => {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved
      ? { ...defaultThemePreference, ...JSON.parse(saved) }
      : defaultThemePreference;
  } catch (error) {
    return defaultThemePreference;
  }
};

const hexToRgba = (hex, alpha) => {
  const clean = hex.replace("#", "");
  const full = clean.length === 3
    ? clean
        .split("")
        .map((char) => `${char}${char}`)
        .join("")
    : clean;

  const bigint = Number.parseInt(full, 16);
  const red = (bigint >> 16) & 255;
  const green = (bigint >> 8) & 255;
  const blue = bigint & 255;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const resolvePreset = (themeId) =>
  themePresets.find((preset) => preset.id === themeId) || themePresets[0];

const resolveTemplate = (templateId) =>
  designTemplates.find((template) => template.id === templateId) || designTemplates[0];

const normalizeTemplateId = (templateId) => {
  if (templateId === "studio") return "single";
  if (templateId === "console") return "premium";
  return resolveTemplate(templateId).id;
};

export const ThemeProvider = ({ children }) => {
  const [preferences, setPreferences] = React.useState(loadStoredPreference);
  const [panelOpen, setPanelOpen] = React.useState(false);

  React.useEffect(() => {
    const preset = resolvePreset(preferences.themeId);
    const templateId = normalizeTemplateId(preferences.templateId);
    const template = resolveTemplate(templateId);
    const accent = preferences.accent || preset.accent;
    const accentSoft = preferences.accentSoft || preset.accentSoft;
    const baseTokens = modeTokens[preferences.mode] || modeTokens.dark;
    const tokens = {
      ...baseTokens,
      ...(templateTokens[template.id] || {}),
    };
    const root = document.documentElement;

    root.dataset.themeMode = preferences.mode;
    root.dataset.designTemplate = template.id;
    root.classList.toggle("dark", preferences.mode === "dark");
    root.style.setProperty("--bg", tokens.background);
    root.style.setProperty("--bg-secondary", tokens.backgroundSecondary);
    root.style.setProperty("--text-primary", tokens.text);
    root.style.setProperty("--text-muted", tokens.textMuted);
    root.style.setProperty("--surface", tokens.surface);
    root.style.setProperty("--surface-strong", tokens.surfaceStrong);
    root.style.setProperty("--surface-soft", tokens.surfaceSoft);
    root.style.setProperty("--surface-border", tokens.border);
    root.style.setProperty("--shadow-color", tokens.shadow);
    root.style.setProperty("--bg-overlay", tokens.overlay);
    root.style.setProperty("--accent", accent);
    root.style.setProperty("--accent-soft", accentSoft);
    root.style.setProperty("--accent-glow", hexToRgba(accent, 0.28));
    root.style.setProperty("--accent-glow-soft", hexToRgba(accentSoft, 0.24));
    const pageGradient =
      template.id === "single"
        ? `linear-gradient(180deg, ${tokens.background} 0%, ${tokens.backgroundSecondary} 100%)`
        : template.id === "premium"
          ? `linear-gradient(135deg, ${tokens.background} 0%, #160f08 48%, ${hexToRgba(accent, 0.22)} 100%)`
          : `linear-gradient(135deg, ${hexToRgba(accent, preferences.mode === "dark" ? 0.18 : 0.14)} 0%, ${tokens.background} 40%, ${hexToRgba(accentSoft, preferences.mode === "dark" ? 0.16 : 0.12)} 100%)`;

    root.style.setProperty("--page-gradient", pageGradient);

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  }, [preferences]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      saveThemePreferenceRemote({
        ...preferences,
        updatedAt: new Date().toISOString(),
      }).catch(() => {});
    }, 500);

    return () => window.clearTimeout(timer);
  }, [preferences]);

  const applyPreset = React.useCallback((presetId) => {
    const preset = resolvePreset(presetId);
    setPreferences((current) => ({
      ...current,
      themeId: preset.id,
      accent: preset.accent,
      accentSoft: preset.accentSoft,
      glow: preset.glow,
    }));
  }, []);

  const toggleMode = React.useCallback(() => {
    setPreferences((current) => ({
      ...current,
      mode: current.mode === "dark" ? "light" : "dark",
    }));
  }, []);

  const applyTemplate = React.useCallback((templateId) => {
    const template = resolveTemplate(normalizeTemplateId(templateId));
    setPreferences((current) => ({
      ...current,
      templateId: template.id,
    }));
  }, []);

  const updateCustomColor = React.useCallback((key, value) => {
    setPreferences((current) => ({
      ...current,
      [key]: value,
    }));
  }, []);

  const value = React.useMemo(
    () => ({
      preferences: {
        ...preferences,
        templateId: normalizeTemplateId(preferences.templateId),
      },
      presets: themePresets,
      templates: designTemplates,
      panelOpen,
      setPanelOpen,
      applyPreset,
      applyTemplate,
      toggleMode,
      updateCustomColor,
    }),
    [preferences, panelOpen, applyPreset, applyTemplate, toggleMode, updateCustomColor]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = React.useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used inside ThemeProvider.");
  }

  return context;
};
